export interface ColDef {
  label: string;
  roles: string[];
  keySuffix?: string;
  anomalyCheck?: 'direct';
}

export const POOL_COLORS: Record<string, string> = {
  BACKEND: 'blue',
  FINANCE_DATA: 'cyan',
  MARKETING_ACQUISITION: 'purple',
  SALES: 'gold',
  MEDICAL_OPS: 'green',
  MIDDLE_MGMT: 'volcano',
  EXECUTIVE: 'red',
};

export const POOL_DESC: Record<string, string> = {
  BACKEND: '5维评价：自评 + 直属 + 斜线 + 本部门同事 + 跨部门同事',
  FINANCE_DATA: '5维评价：自评 + 直属 + 斜线 + 本部门同事 + 跨部门同事',
  MARKETING_ACQUISITION: '5维评价：自评 + 直属 + 斜线 + 本部门同事 + 跨部门同事',
  SALES: '5维评价：自评 + 直属 + 斜线 + 本部门同事 + 跨部门同事',
  MEDICAL_OPS: '5维评价：自评 + 直属 + 斜线 + 本部门同事 + 跨部门同事',
  MIDDLE_MGMT: '5维评价：自评 + 直属 + 斜线 + 下属 + 同级同事',
  EXECUTIVE: '5维评价：自评 + 直属领导 + 斜线领导 + 同级同事 + 下属（高管池互评）',
};

const STANDARD_COLS: ColDef[] = [
  { label: '自评', roles: ['SELF'] },
  { label: '直属领导', roles: ['DIRECT'], anomalyCheck: 'direct' },
  { label: '斜线领导/带教老师', roles: ['DIAGONAL'] },
  { label: '本部门同事', roles: ['SAME_DEPT'] },
  { label: '跨部门同事', roles: ['CROSS_DEPT'] },
];

export const POOL_COL_DEFS: Record<string, ColDef[]> = {
  BACKEND: STANDARD_COLS,
  FINANCE_DATA: STANDARD_COLS,
  MARKETING_ACQUISITION: STANDARD_COLS,
  SALES: STANDARD_COLS,
  MEDICAL_OPS: STANDARD_COLS,
  MIDDLE_MGMT: [
    { label: '自评', roles: ['SELF'] },
    { label: '直属领导', roles: ['DIRECT'], anomalyCheck: 'direct' },
    { label: '斜线领导/带教老师', roles: ['DIAGONAL'] },
    { label: '下属', roles: ['SUBORDINATE'] },
    { label: '同级同事', roles: ['PEER_LEADER'] },
  ],
  EXECUTIVE: [
    { label: '自评', roles: ['SELF'] },
    { label: '直属领导', roles: ['DIRECT'], anomalyCheck: 'direct' },
    { label: '斜线领导', roles: ['OTHER_LEADER'], keySuffix: 'a' },
    { label: '同级同事', roles: ['OTHER_LEADER'], keySuffix: 'b' },
    { label: '下属', roles: ['SUBORDINATE', 'OTHER_LEADER'] },
  ],
};

export const POOL_ROLE_OPTIONS: Record<string, string[]> = Object.fromEntries(
  Object.entries(POOL_COL_DEFS).map(([pool, cols]) => [
    pool,
    Array.from(new Set(cols.flatMap((col) => col.roles))),
  ]),
);

/** 表头列的规范角色顺序：系统角色在前，自定义角色按 code 排在后面。 */
const ROLE_COLUMN_ORDER = [
  'SELF',
  'DIRECT',
  'DIAGONAL',
  'SAME_DEPT',
  'CROSS_DEPT',
  'SUBORDINATE',
  'PEER_LEADER',
  'OTHER_LEADER',
];

function roleOrderIndex(role: string): number {
  const index = ROLE_COLUMN_ORDER.indexOf(role);
  return index === -1 ? ROLE_COLUMN_ORDER.length : index;
}

/**
 * 完全由模板 relationWeights 生成列：一个角色一列（自定义人才池走这里，
 * 不再 fallback 到 BACKEND 的固定 5 列——避免出现「模板里没有的角色一直缺失·补派」）。
 */
export function buildRoleDrivenColDefs(
  pool: string,
  relationWeights: Record<string, number>,
  getLabel: (pool: string, role: string) => string,
): ColDef[] {
  return Object.keys(relationWeights)
    .sort((a, b) => roleOrderIndex(a) - roleOrderIndex(b) || a.localeCompare(b))
    .map((role) => ({
      label: getLabel(pool, role) || role,
      roles: [role],
      ...(role === 'DIRECT' ? { anomalyCheck: 'direct' as const } : {}),
    }));
}

export function buildPoolColDefs(
  pool: string,
  relationWeights: Record<string, number> | undefined,
  getLabel: (pool: string, role: string) => string,
): ColDef[] {
  const base = POOL_COL_DEFS[pool];
  if (!base) {
    // 自定义人才池：有模板就完全按模板角色出列，没有模板才回退旧 5 列
    if (relationWeights && Object.keys(relationWeights).length) {
      return buildRoleDrivenColDefs(pool, relationWeights, getLabel);
    }
    return POOL_COL_DEFS.BACKEND;
  }
  if (!relationWeights) return base;
  const covered = new Set(base.flatMap((col) => col.roles));
  const extras = Object.keys(relationWeights)
    .filter((role) => !covered.has(role))
    .sort();
  if (!extras.length) return base;
  return [
    ...base,
    ...extras.map((role) => ({
      label: getLabel(pool, role),
      roles: [role],
      keySuffix: `extra-${role}`,
    })),
  ];
}

/** 池说明文案：系统池用固定文案，自定义池按实际列拼出来。 */
export function poolDescOf(pool: string, cols: ColDef[]): string {
  return POOL_DESC[pool] ?? `${cols.length}维评价：${cols.map((col) => col.label).join(' + ')}`;
}

/**
 * 评价角色字典总开关：字典里停用的角色，列整体不显示。
 * 多角色列（如高管池「下属/其他领导」混列）只要还有一个角色启用就保留。
 */
export function filterColsByDisabledRoles(
  cols: ColDef[],
  disabledRoles: ReadonlySet<string>,
): ColDef[] {
  if (!disabledRoles.size) return cols;
  return cols.filter((col) => col.roles.some((role) => !disabledRoles.has(role)));
}

export function getDefaultCreatePick(
  evaluateeId: number,
  col: ColDef,
): { evaluatorId: number | undefined; role: string } {
  const role = col.roles[0];
  return {
    evaluatorId: role === 'SELF' ? evaluateeId : undefined,
    role,
  };
}
